import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";

import Container from "../components/common/Container";
import { API } from "../api";
import useLocalStorage from "../hooks/useLocalStorage";

/**
 * TelegramCallback Page
 * 
 * Handles the OAuth callback when a user links their FunDeSci account to Telegram.
 * Requires the user to be logged in.
 */
const TelegramCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { value: token } = useLocalStorage("token");

  const [status, setStatus] = useState("processing"); // processing, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const processCallback = async () => {
      const state = searchParams.get("state");
      const telegramId = searchParams.get("telegramId");

      if (!state || !telegramId) {
        setStatus("error");
        setErrorMessage("Invalid callback parameters. Please try again from Telegram.");
        return;
      }

      if (!token) {
        // User needs to log in first
        setStatus("error");
        setErrorMessage("Please log in to your FunDeSci account first, then try again from Telegram.");
        return;
      }

      try {
        await API.post(
          "/telegram/verify/fundesci",
          { state },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          err.response?.data?.message || 
          "Failed to link your account. The link may have expired. Please try again from Telegram."
        );
      }
    };

    processCallback();
  }, [searchParams, token]);

  return (
    <Container>
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Link to Telegram</h1>
          </div>

          {/* Processing */}
          {status === "processing" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <Oval
                height={48}
                width={48}
                color="#6366f1"
                secondaryColor="#a5b4fc"
                strokeWidth={4}
                strokeWidthSecondary={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-gray-600">Linking your account to Telegram...</p>
            </div>
          )}

          {/* Success */}
          {status === "success" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Account Linked!</h2>
              <p className="text-gray-600 mb-6">
                Your FunDeSci account has been linked to your Telegram account.
                You can close this page and return to Telegram.
              </p>
              <p className="text-sm text-gray-500">
                Tap "Check Status" in the Telegram app to see your communities.
              </p>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Link Failed</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              
              {!token && (
                <button
                  onClick={() => {
                    // Redirect to /login preserving state+telegramId so the flow
                    // can complete after login instead of starting from scratch.
                    const returnUrl = window.location.href;
                    window.location.href = `/login?redirect=${encodeURIComponent(returnUrl)}`;
                  }}
                  className="w-full bg-indigo-500 text-white rounded-xl p-4 font-medium hover:bg-indigo-600 transition-colors"
                >
                  Log In
                </button>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Powered by FunDeSci
          </p>
        </div>
      </div>
    </Container>
  );
};

export default TelegramCallback;
